import { Button, Drawer, Form, Input, InputNumber, Select, Space } from "antd";
import { useEffect, useState } from "react";
import { IProcurementItem } from "../../../apis/procurement.api";
import { getVendorCached } from "../../../apis/vendor.api";
import { VendorWarehouseForm } from "../../../pages/vendors/form/warehouse";
import React, { forwardRef, useImperativeHandle } from "react";

export interface ProcurementItemFormInner {
  initData?: Partial<IProcurementItem>;
  onSubmit: (values: Record<string, string | number>) => void;
  vendors: any[];
  useQuantityField?: boolean;
  originalQuantity?: number;
  warehouseSelectRef?: React.Ref<any>;
  vendorSelectRef?: React.Ref<any>;
  context?: 'cart' | 'procurement';
  /**
   * Vendors already on the row (the cart line's `variant.vendor_data`), each
   * with its active `warehouses`. When the selected vendor is among them the
   * warehouse dropdown is filled from here and no GET /vendor/<id> is made.
   */
  knownVendors?: any[];
  /**
   * Open stock-PO lines for the row's product (`incoming_purchase_orders` on the row).
   * When given, the form shows an optional "Incoming PO" picker bound to `incoming_po_item`;
   * when omitted the field stays hidden and is set from the row's panel instead (the cart page).
   */
  incomingPos?: any[];
  /** Fires when the Incoming PO picker changes, so a parent mirroring the choice stays in step. */
  onIncomingPoChange?: (id: number | null) => void;
}

enum RemarkType {
  DISCONTINUED = "Discontinued",
  DISTINCT_LEAD_TIMES = "Distinct Lead Times",
  OTHERS = "Others",
}

const ProcurementItemFormInner = (
  {
    initData = {},
    onSubmit,
    vendors,
    useQuantityField,
    originalQuantity,
    warehouseSelectRef,
    vendorSelectRef,
    context = 'procurement',
    knownVendors,
    incomingPos,
    onIncomingPoChange,
  }: ProcurementItemFormInner,
  ref: React.Ref<any>
) => {

  console.log("initData -> ", initData);

  const [form] = Form.useForm();


  const [remark, setRemark] = useState<string>(initData?.remarks ?? "");
  const [leadTime, setLeadTime] = useState<number | null | undefined>(
    initData?.lead_time ?? initData?.predicted_lead_time
  );
  //const [quantity, setQuantity] = useState<number | null | undefined>(initData?.quantity);
  const [warehouseDropdownOpen, setWarehouseDropdownOpen] = useState(false);
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);

  useEffect(() => {
    form.setFieldsValue({ lead_time: leadTime });
  }, [leadTime, form]);

   const [selectedVendor, setSelectedVendor] = useState<number | undefined>(initData?.selected_vendor_id ?? initData?.vendor?.[0]?.id);
  //const [selectedVendor, setSelectedVendor] = useState<number | undefined>(initData?.selected_vendor_id);
 
  const [showWarehouseDrawer, setShowWarehouseDrawer] = useState(false);
  const [remarkType, setRemarkType] = useState<RemarkType>(RemarkType.OTHERS);
  const [selectedVendorDetails, setSelectedVendorDetails] = useState<any | null>(null);

  // Expose form instance to parent
  useImperativeHandle(ref, () => ({
    getFieldsValue: () => form.getFieldsValue(),
    validateFields: () => form.validateFields(),
    setLeadTime: (value: number) => setLeadTime(value),
    setQuantity: (value: number) => form.setFieldValue("quantity", value),
    // The incoming stock-PO line (mirror row id) picked from the row's panel; null clears it.
    setIncomingPoItem: (value: number | null) => form.setFieldValue("incoming_po_item", value),
    openWarehouseDropdown: () => setWarehouseDropdownOpen(true),
    openVendorDropdown: () => setVendorDropdownOpen(true),
  }));

  const remarkHelperField = () => {
    
    const quantity = form.getFieldValue("quantity");

    const restQuantity = Number(originalQuantity) - Number(quantity ?? 0);

    if (remarkType === RemarkType.DISCONTINUED) {
      form.setFieldValue(
        "remarks",
        `Remaining ${restQuantity} items are discontinued`
      );
      return null;
    }
    
    if (remarkType === RemarkType.DISTINCT_LEAD_TIMES) {
      return (
        <InputNumber
          placeholder="Lead Time for rest"
          size="small"
          style={{ width: 100 }}
          onChange={(e) => {
            form.setFieldValue(
              "remarks",
              `Remaining ${restQuantity} items have lead time of ${e} days`
            );
          }}
        />
      );
    }
  
    if (remarkType === RemarkType.OTHERS) {
      return null;
    }

    return null;
  };

  useEffect(() => {
    if (!selectedVendor) {
      setSelectedVendorDetails(null);
      return;
    }

    const applyVendor = (data: any) => {
      setSelectedVendorDetails(data);
      setTimeout(() => {
        if (data?.warehouses?.length === 1) {
          form.setFieldsValue({
            selected_vendor_ware_house: data?.warehouses[0]?.id,
          });
        }
      }, 100);
    };

    // The row already carries its candidate vendors with their warehouses
    // (vendor_data): no request at all when the selected vendor is one of them.
    const known = (knownVendors ?? []).find(
      (v: any) => Number(v?.id) === Number(selectedVendor) && Array.isArray(v?.warehouses)
    );
    if (known) {
      applyVendor(known);
      return;
    }

    // Otherwise (a vendor picked from the full list) fetch it, cached per
    // vendor so rows sharing one make a single request and remounts make none.
    let cancelled = false;
    getVendorCached(selectedVendor).then((data) => {
      if (!cancelled) applyVendor(data);
    });
    return () => {
      cancelled = true;
    };
  }, [form, selectedVendor, knownVendors]);

  useEffect(() => {
    if (form && useQuantityField) {
      form.setFieldValue("quantity", initData?.quantity ?? initData?.predicted_quantity);
    }
  }, [form, useQuantityField, initData]);


  useEffect(() => {
    console.log("d>>>", initData);
  }, [initData]);
  return (
    <>
      <Form
        onFinish={onSubmit}
        initialValues={{
          lead_time: initData?.lead_time ?? initData?.predicted_lead_time,
          remarks: initData?.remarks,
          total_stock: initData?.total_stock ?? 0,
          selected_vendor: selectedVendor,
          selected_vendor_ware_house: initData?.selected_vendor_ware_house_id,
          incoming_po_item: initData?.incoming_po_item ?? null,
        }}
        key={initData?.id}
        layout="inline"
        form={form}
      >
        {/* Set from the row's incoming-PO panel ("Use this PO") when no list is given here. */}
        {incomingPos === undefined && (
          <Form.Item name="incoming_po_item" hidden>
            <Input type="hidden" />
          </Form.Item>
        )}
        <Space className="my-1">
          {incomingPos !== undefined && (
            <div>
              <div style={{ fontSize: 9, marginBottom: 1 }}>Incoming PO</div>
              <Form.Item name="incoming_po_item">
                <Select
                  placeholder={incomingPos.length ? "Optional" : "No open stock PO"}
                  disabled={!incomingPos.length}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  style={{ minWidth: 180, maxWidth: 300 }}
                  size="small"
                  options={incomingPos.map((row: any) => ({
                    value: row.id,
                    label: `${row.purchase_order_number || `PO line ${row.id}`} · ${Number(
                      row.quantity ?? 0
                    )} incoming${row.delivery_date ? ` · due ${row.delivery_date}` : ""}`,
                  }))}
                  onChange={(v) => onIncomingPoChange?.(v == null ? null : Number(v))}
                />
              </Form.Item>
            </div>
          )}

          <div ref={vendorSelectRef}>
            <div style={{ fontSize: 9, marginBottom: 1 }}>Vendor</div>

            <Form.Item
              name="selected_vendor"
              rules={[{ required: true, message: "Please select vendor" }]}
            >
             
              <Select
                placeholder="Vendor"
                style={context === 'cart' ? { minWidth: 350, maxWidth: 350 } : { minWidth: 175, maxWidth: 175 }}

                 open={vendorDropdownOpen}
                 onDropdownVisibleChange={setVendorDropdownOpen}
               
                filterOption={(input, option) =>
                  (String(option?.label) ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
               
                options={vendors.map((vendor: any) => ({
                  label: `${vendor?.name}`,
                  value: vendor?.id,
                }))}

                value={selectedVendor}
                onChange={(value) => {
                  setSelectedVendor(value);
                  form.setFieldsValue({ selected_vendor_ware_house: null });
                }}
                showSearch
                size="small"
              />
            </Form.Item>
          </div>

          {selectedVendorDetails && selectedVendorDetails?.warehouses?.length > 0 ? (
            <div ref={warehouseSelectRef}>
              <div style={{ fontSize: 9, marginBottom: 1 }}>Warehouse</div>
              <Form.Item
                name="selected_vendor_ware_house"
                rules={[{ required: true, message: "Please select warehouse" }]}
              >
                <Select
                  placeholder="Vendor Warehouse"
                  style={context === 'cart' ? { minWidth: 260, maxWidth: 400 } : { minWidth: 205, maxWidth: 375 }}
                  open={warehouseDropdownOpen}
                  onDropdownVisibleChange={setWarehouseDropdownOpen}
                  filterOption={(input, option) =>
                    (String(option?.label) ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  
                  options={(selectedVendorDetails?.warehouses ?? []).map(
                    (warehouse: any) => ({
                      label: `${warehouse?.name}`,
                      value: warehouse?.id,
                    })
                  )}
                 
                  showSearch
                  size="small"
                />
              </Form.Item>
            </div>
          ) : (
            <Button
              type="dashed"
              size="small"
              onClick={() => setShowWarehouseDrawer(true)}
            >
              Add Warehouse
            </Button>
          )}
          {/* {selectedVendor &&
          selectedVendorDetails &&
          selectedVendorDetails?.warehouse?.length === 0 && (
            <Button size="small">Add Warehouse</Button>
          )} */}
          
          {useQuantityField && (
            <div>
              <div style={{ fontSize: 9, marginBottom: 1 }}>Avl. Quantity</div>
              <Form.Item
                name="quantity"
                rules={[
                  {
                    validator: (_, value) =>
                      value !== undefined && value !== null && value !== "" && (context === 'cart' ? value>=0 : value > 0)
                        ? Promise.resolve()
                        : Promise.reject(new Error("Please enter a valid quantity")),
                  },
                ]}
              >
                <InputNumber
                  placeholder="Quantity"
                  size="small"
                  value={initData?.quantity}

                  onChange={(e) => {
                    form.setFieldValue("quantity", e);
                    const val = Number(e);

                    if (remarkType === RemarkType.OTHERS) return;
                    if (remarkType === RemarkType.DISCONTINUED && originalQuantity) {
                      form.setFieldValue(
                        "remarks",
                        `Remaining ${
                          Number(originalQuantity) - val
                        } items are discontinued`
                      );
                    }
                    if (
                      remarkType === RemarkType.DISTINCT_LEAD_TIMES &&
                      originalQuantity
                    ) {
                      form.setFieldValue(
                        "remarks",
                        `Remaining ${
                          Number(originalQuantity) - val
                        } items have lead time of ${form.getFieldValue(
                          "remarks"
                        )} days`
                      );
                    }
                  }}

                />
              </Form.Item>
            </div>
          )}
         
          <div>
            <div style={{ fontSize: 9, marginBottom: 1 }}>Lead Time</div>
           
            <Form.Item
              name="lead_time"
              rules={[
                {
                  required: true,
                  message: "Please enter quantity",
                },
              ]}
            >
              <InputNumber
                placeholder="Lead Time"
                size="small"
                value={leadTime}
                onChange={(val) => setLeadTime(val)}
              />
            </Form.Item>
         
          </div>

          <div className="me-2">
       
            <div style={{ fontSize: 9, marginBottom: 1 }}>Remarks</div>

            <div className="d-flex gap-1 align-items-center">
             
              <Select
                style={{ minWidth: 100, marginBottom: 0 }}
                value={remarkType}
                onChange={(value) => {
                  setRemarkType(value);
                  if (value === RemarkType.OTHERS) {
                    form.setFieldValue("remarks", "");
                  }
                }}
                size="small"
                options={
                  Object.values(RemarkType).map((value) => ({
                    label: value,
                    value,
                  })) as any
                }
              />
              
              {remarkHelperField()}
             
              <Form.Item
                name="remarks"
                style={{
                  minWidth: 150,
                  display: remarkType !== RemarkType.OTHERS ? "none" : "block",
                }}
              >
             
                <Input  
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Remarks" 
                size="small" 
                />
                
              </Form.Item>
           
            </div>
          </div>
         
          {/* <Form.Item name="total_stock">
          <Input placeholder="Total Stock" />
        </Form.Item> */}

          <Form.Item>
            <Button htmlType="submit" type="primary">
              {initData?.lead_time ? "Edit" : "Submit"}
            </Button>
          </Form.Item>
        </Space>
      </Form>

      <Drawer
        open={showWarehouseDrawer}
        title="Add Vendor Warehouse"
        onClose={() => {
          setShowWarehouseDrawer(false);
          // Re fetch vendor details
          if (selectedVendor) {
            getVendorCached(selectedVendor, { force: true }).then((data) => {
              setSelectedVendorDetails(data);
            });
          }
        }}
      >
        <VendorWarehouseForm
          vendor={selectedVendorDetails}
          onClose={() => {
            setShowWarehouseDrawer(false);
            // Re fetch vendor details
            if (selectedVendor) {
              getVendorCached(selectedVendor, { force: true }).then((data) => {
                setSelectedVendorDetails(data);
              });
            }
          }}
        />
      </Drawer>
    </>
  );
};

export const ProcurementItemForm = forwardRef(ProcurementItemFormInner);
